// Client-side CSV export for the admin Reporting "AHT by account per rep"
// section (2026-09-29, Patrick's request -- described as "export to Excel,"
// built as CSV since that's what every other export in this codebase does
// and Excel opens it natively). Built directly from data already loaded in
// the browser (ReportingPage.jsx's ahtRepData plus the current agent-filter
// selection), not a server round trip, so the export always exactly matches
// the currently selected agents/granularity/periods-back -- but NOT the
// collapsed/expanded period display state, which is a pure UI toggle: every
// period's agent rows are always included in the export regardless of what
// happens to be collapsed on screen right now.
//
// One row per (period, agent), plus a "Total" row per period and one final
// grand-Total row -- mirrors the visible table's own row structure exactly,
// pooling through the same poolAgentRows() the UI's own Total rows already
// use, so the export can never disagree with what's on screen.

import { poolAgentRows } from './ahtByRepPooling.js'

// Quotes a field only when it needs it (RFC 4180): a comma, a double quote,
// or a newline -- doubling any internal quote. Mirrors
// services/weekly-export.js's own csvEscape() server-side; not shared
// across the server/ui package boundary, same "small enough not to need
// sharing" reasoning that file's own header comment gives for its own
// format helpers.
function csvEscape(value) {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (/["\n\r,]/.test(str)) return `"${str.replace(/"/g, '""')}"`
  return str
}

// ms -> "H:MM:SS" or "M:SS", matching ReportingPage.jsx's own
// formatHandleMs() -- duplicated rather than imported (that function isn't
// exported, and it's small); empty string for null, never that function's
// own '—' placeholder, since an empty CSV cell is the null-handling
// convention every server-built export in this codebase already uses.
function formatAhtMs(ms) {
  if (typeof ms !== 'number' || !isFinite(ms)) return ''
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

function buildHeaders(accountKeys, accountLabels) {
  return [
    'Period', 'Agent', 'Total Contacts', 'Combined AHT',
    ...accountKeys.map((a) => `${accountLabels[a]} Contacts`),
    ...accountKeys.map((a) => `${accountLabels[a]} AHT`),
  ]
}

function agentRowToCsvRow(periodLabel, agentLabel, row, accountKeys) {
  return [
    periodLabel, agentLabel, row.totalContacts, formatAhtMs(row.combinedAht),
    ...accountKeys.map((a) => row.byAccount[a]?.contacts ?? 0),
    ...accountKeys.map((a) => formatAhtMs(row.byAccount[a]?.aht ?? null)),
  ]
}

// periods: ahtRepData.periods, every period regardless of collapsed state.
// activeAgents: the currently agent-filter-selected agent list
// (activeAhtRepAgents in ReportingPage.jsx). accountKeys/accountLabels:
// AHT_REP_ACCOUNTS/AHT_REP_ACCOUNT_LABELS from the same file.
export function buildAhtByAccountPerRepCsv({ periods, activeAgents, accountKeys, accountLabels }) {
  const headers = buildHeaders(accountKeys, accountLabels)
  const lines = [headers.map(csvEscape).join(',')]

  for (const period of periods) {
    const shownAgents = period.agents.filter((a) => activeAgents.includes(a.agent))
    for (const a of shownAgents) {
      lines.push(agentRowToCsvRow(period.label, a.agent, a, accountKeys).map(csvEscape).join(','))
    }
    const periodTotal = poolAgentRows(shownAgents, accountKeys)
    lines.push(agentRowToCsvRow(period.label, 'Total', periodTotal, accountKeys).map(csvEscape).join(','))
  }

  const grandTotal = poolAgentRows(
    periods.flatMap((period) => period.agents.filter((a) => activeAgents.includes(a.agent))),
    accountKeys,
  )
  lines.push(agentRowToCsvRow('All periods', 'Total', grandTotal, accountKeys).map(csvEscape).join(','))

  // CRLF (RFC 4180) -- what Excel/Sheets expect a CSV to use, same as
  // services/weekly-export.js's own rowsToCsv().
  return lines.join('\r\n') + '\r\n'
}

// Triggers a browser download of the given CSV text -- same Blob -> object
// URL -> hidden <a download> -> revoke pattern api.js's downloadCsv() uses
// for a server-fetched CSV, just skipping the fetch since this one's built
// entirely client-side already.
export function downloadCsvText(csvText, filename) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
